import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import UploadKdfModal from "../../../Modals/UploadKdfModal";
import KDFSettingsPageListing from "./KDFSettingsPageListing";
import noImage from "../../../../../assets/images/no-image.png";
import Button from "../../../Elements/Button";
import apiGETRequest from "../../../../utils/apiRequest";
import CommonModal from "../../../Modals/CommonModal";
import { Col, Form, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faFloppyDisk,
  faRectangleXmark,
  faQuestion,
} from "@fortawesome/free-solid-svg-icons";
import "./KdfSettings.css";
import { apiJsonRequest } from "../../../../utils/apiRequest";
import customAPIRequest from "../../../../utils/customAPIRequest";
import CustomTooltip from "../../../Elements/Tooltip";
import { networkService } from "../../../config/axios";
import TagChipEditor from "../../../CommonComponents/Tags/TagChipEditor";
import { resolveTags } from "../../../../utils/resolveTags";
import UserInfoContext from "../../../../utils/userInfoContext";
import ColorPicker from "../../../Elements/ColorPicker";

const DEFAULT_BG = { bg_color: '#252525', pattern_color: '#BDB4AE', pattern_opacity: 0.5, pattern_type: 'herringbone' };

const BG_PATTERNS = [
  { id: 'none',         name: 'None' },
  { id: 'dotted_grid',  name: 'Dotted Grid' },
  { id: 'diamond_mesh', name: 'Diamond Mesh' },
  { id: 'wave',         name: 'Wave' },
  { id: 'chevron',      name: 'Chevron' },
  { id: 'hexagonal',    name: 'Hexagonal Tile' },
  { id: 'cross_weave',  name: 'Cross Weave' },
  { id: 'basket_weave', name: 'Basket Weave' },
  { id: 'herringbone',  name: 'Herringbone' },
];

const buildPatternStyle = (type, bg, pc, opacity) => {
  const alpha = Math.round(opacity * 255).toString(16).padStart(2, '0');
  const stroke = `${pc}${alpha}`;
  const defs = {
    dotted_grid:  { w: 12, h: 12, t: 'scale(1)',               el: `<circle cx='2' cy='2' r='1' fill='${stroke}'/>` },
    diamond_mesh: { w: 16, h: 16, t: 'scale(1)',               el: `<path d='M8 0 L16 8 L8 16 L0 8 Z' fill='none' stroke='${stroke}' stroke-width='1'/>` },
    wave:         { w: 24, h: 12, t: 'scale(1)',               el: `<path d='M0 6 Q6 0 12 6 T24 6' fill='none' stroke='${stroke}' stroke-width='1'/>` },
    chevron:      { w: 18, h: 18, t: 'scale(1)',               el: `<path d='M0 4 L9 13 L18 4' fill='none' stroke='${stroke}' stroke-width='1.5'/>` },
    hexagonal:    { w: 20, h: 18, t: 'scale(1)',               el: `<path d='M5 1 L15 1 L19 9 L15 17 L5 17 L1 9 Z' fill='none' stroke='${stroke}' stroke-width='1'/>` },
    cross_weave:  { w: 16, h: 16, t: 'scale(1)',               el: `<path d='M8 0 V16 M0 8 H16' stroke='${stroke}' stroke-width='1'/>` },
    basket_weave: { w: 20, h: 20, t: 'scale(1)',               el: `<path d='M0 5 H20 M0 15 H20 M5 0 V20 M15 0 V20' stroke='${stroke}' stroke-width='2'/>` },
    herringbone:  { w: 40, h: 40, t: 'scale(0.7) rotate(45)', el: `<path d='M0 0h10v20H0zM30 0v10H10V0zM10 10h10v20H10zM40 10v10H20V10zM20 20h10v20H20zM50 20v10H30V20zM30 30h10v20H30zM20 30v10H0V30zM10 20v10h-20V20zM30-10h10v20H30z' stroke-width='1' stroke='${stroke}' fill='none'/>` },
  };
  if (type === 'none' || !defs[type]) return { backgroundColor: bg };
  const p = defs[type];
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${p.w}' height='${p.h}'><defs><pattern id='a' patternUnits='userSpaceOnUse' width='${p.w}' height='${p.h}' patternTransform='${p.t}'><rect width='100%' height='100%' fill='${bg}'/>${p.el}</pattern></defs><rect width='100%' height='100%' fill='url(#a)'/></svg>`;
  return { backgroundColor: bg, backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(svg)}")` };
};

const KdfSettings = ({ docs }) => {
  const [uploadPDFModalStatus, setUploadPDFModalStatus] = useState(false);
  const [deletePDFActiveState, setDeletePDFActiveState] = useState(null);
  const [privatePDFActiveState, setPrivatePDFActiveState] = useState(null);
  const [replaceSelectedPDFState, setReplaceSelectedPDFState] = useState(null);
  const [docSettingsData, setDocSettingsData] = useState({
    engagementThreshold: {
      status: false,
      value: 0,
    },
    notes: {
      status: false,
      value: "",
    },
  });
  const settingsNoteRef = useRef(null);
  const originalNoteValueRef = useRef(null); // Store original note value for cancel
  const uploadModalRef = useRef(null);
  const uploadModalButtonRef = useRef(null);
  const uploadInProgressRef = useRef(false);
  const previousProcessingCountRef = useRef(0);
  const thumbnailAtUploadStartRef = useRef(null);
  const pollingTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(3000); // Start with 3s, will adapt
  const { setShowNotifications, profileData } = useContext(UserInfoContext)
  const editableTitleRef = useRef(null);
  const [editableTitle, setEditableTitle] = useState({
    status: false,
    value: docs?.title || "",
    id: docs?.id,
  });

  // State to remember textarea height
  const [noteTextareaHeight, setNoteTextareaHeight] = useState(() => {
    try {
      const saved = localStorage.getItem('kdf_note_textarea_height');
      return saved ? parseInt(saved) : 54;
    } catch {
      return 54;
    }
  });

  const lastEditionStorageKey = docs?.id ? `kdf_${docs.id}_last_edition` : null;

  const [selectedEditionId, setSelectedEditionId] = useState(() => {
    if (lastEditionStorageKey) {
      try {
        const saved = localStorage.getItem(lastEditionStorageKey);
        if (saved) return Number(saved);
      } catch {}
    }
    return docs?.thumbnail_docs_file_id ?? docs?.doc_files_languages?.[0]?.file_id ?? null;
  });
  const [selectedEdition, setSelectedEdition] = useState(null);
  const selectedEditionIdRef = useRef(selectedEditionId);
  selectedEditionIdRef.current = selectedEditionId;
  const selectedEditionLocaleRef = useRef(null);
  if (selectedEdition?.language?.locale) selectedEditionLocaleRef.current = selectedEdition.language.locale;
  const [loadingEdition, setLoadingEdition] = useState(false);
  const editionFetchTokenRef = useRef(0);

  const fetchSelectedEdition = useCallback(async (fileId) => {
    if (!fileId || !docs?.id) return;
    const token = ++editionFetchTokenRef.current;
    setLoadingEdition(true);
    try {
      const data = await apiGETRequest(`docs/${docs.id}/files/${fileId}.json`);
      if (token === editionFetchTokenRef.current && data && !data.error) setSelectedEdition(data);
    } finally {
      if (token === editionFetchTokenRef.current) setLoadingEdition(false);
    }
  }, [docs?.id]);

  useEffect(() => {
    fetchSelectedEdition(selectedEditionId);
  }, [selectedEditionId, fetchSelectedEdition]);

  useEffect(() => {
    const handleVisible = () => {
      if (document.visibilityState === "visible") fetchSelectedEdition(selectedEditionId);
    };
    window.addEventListener("focus", handleVisible);
    document.addEventListener("visibilitychange", handleVisible);
    return () => {
      window.removeEventListener("focus", handleVisible);
      document.removeEventListener("visibilitychange", handleVisible);
    };
  }, [selectedEditionId, fetchSelectedEdition]);

  const handleSelectEdition = (fileId) => {
    if (!fileId) return;
    setSelectedEditionId(fileId);
    if (lastEditionStorageKey) {
      try { localStorage.setItem(lastEditionStorageKey, String(fileId)); } catch {}
    }
  };

  const handleEditionTagsChange = async (nextTags) => {
    const resolvedTags = await resolveTags(nextTags, selectedEdition?.kdf_tags || []);
    const tagIds = resolvedTags.map((tag) => tag.id);
    const { data } = await apiJsonRequest(
      `docs/${docs.id}/files/${selectedEditionId}.json`, "PATCH",
      { docs_file: { kdf_tag_ids: tagIds } }
    );
    if (data && !data.error) setSelectedEdition(data);
  };

  const editionThumb = selectedEdition?.thumbs_details
    ? `${selectedEdition.thumbs_details.mobile}0.${selectedEdition.thumbs_details.extension}`
    : null;

  const handleCancelModalEvent = async (status) => {
    if (status == false && docUploadData.pdfList?.length == 0 && !uploadInProgressRef.current) {
      try {
        await networkService(`kdfs/${docs.id}.json`, "DELETE");
      } catch (e) {
        console.error("Failed to discard empty KDF:", e);
      }
      window.location.href = "/kdfs";
    }
  };

  const [imageThumb, setImageThumb] = useState(
    docs?.thumb != ""
      ? docs?.thumb != undefined
        ? docs?.thumb
        : noImage
      : noImage
  );

  // Optimize: Avoid deep clone on initial state
  const [docUploadData, setDocUploadData] = useState({
    image: imageThumb,
    active: false,
    pdfList: docs?.doc_files_languages || [],
    docData: {},
  });

  const updateDocSettings = (dataGet) => {
    let docSettingsDataCopy = JSON.parse(JSON.stringify(docSettingsData));
    docSettingsDataCopy.engagementThreshold.value =
      dataGet.page_viewed_sec ?? 0;
    docSettingsDataCopy.notes.value = dataGet.note ?? "";
    setDocSettingsData({ ...docSettingsDataCopy });
    if (dataGet.viewer_bg_settings) {
      const synced = { ...DEFAULT_BG, ...dataGet.viewer_bg_settings };
      setViewerBgSettings(synced);
      savedBgSettingsRef.current = synced;
    }
  };

  const [viewerBgSettings, setViewerBgSettings] = useState(
    () => ({ ...DEFAULT_BG, ...(docs?.viewer_bg_settings || {}) })
  );
  // Tracks the last-saved/loaded background settings so the Save button can
  // tell whether the current edits actually differ from what's persisted.
  const savedBgSettingsRef = useRef(viewerBgSettings);

  useEffect(() => {
    if (docs?.viewer_bg_settings) {
      const synced = { ...DEFAULT_BG, ...docs.viewer_bg_settings };
      setViewerBgSettings(synced);
      savedBgSettingsRef.current = synced;
    }
  }, [docs?.viewer_bg_settings]);

  const bgDirty = JSON.stringify(viewerBgSettings) !== JSON.stringify(savedBgSettingsRef.current);
  const BG_CLIPBOARD_KEY = 'kdf_bg_settings_clipboard';
  const [bgSaving, setBgSaving] = useState(false);
  const [bgCopied, setBgCopied] = useState(false);
  const [bgPasteStatus, setBgPasteStatus] = useState(null); // 'ok' | 'err'

  const handleBgChange = (field, value) => {
    setViewerBgSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleBgSave = async () => {
    setBgSaving(true);
    try {
      const res = await apiJsonRequest(`kdfs/${docs.id}.json`, 'PATCH', {
        doc: { viewer_bg_settings: viewerBgSettings }
      });
      if (res?.data) {
        const savedSettings = res.data.viewer_bg_settings || viewerBgSettings;
        setViewerBgSettings(savedSettings);
        savedBgSettingsRef.current = savedSettings;
        setDocUploadData(prev => ({ ...prev, docData: res.data }));
      }
    } catch (e) { console.warn(e); }
    setBgSaving(false);
  };

  const handleBgCopy = () => {
    const json = JSON.stringify(viewerBgSettings);
    localStorage.setItem(BG_CLIPBOARD_KEY, json);
    try { navigator.clipboard.writeText(json)?.catch((err) => console.error(err)); } catch (err) { console.error(err); }
    setBgCopied(true);
    setTimeout(() => setBgCopied(false), 2000);
  };

  const handleBgPaste = () => {
    let parsed = null;
    try {
      const stored = localStorage.getItem(BG_CLIPBOARD_KEY);
      if (stored) parsed = JSON.parse(stored);
    } catch (err) { console.error(err); }
    if (parsed && parsed.bg_color && parsed.pattern_color && parsed.pattern_opacity !== undefined) {
      setViewerBgSettings({ ...DEFAULT_BG, ...parsed });
      setBgPasteStatus('ok');
    } else {
      setBgPasteStatus('err');
    }
    setTimeout(() => setBgPasteStatus(null), 2000);
  };

  const updateDocUploadData = async (status = null) => {
    try {
      // Optimize: Use shallow copy instead of expensive deep clone
      let docUploadDataCopy = { ...docUploadData };
      let fetchUploadData = await apiGETRequest(`docs/${docs.id}/files.json`);
      
      // Track if this is a new upload (not a reload)
      if (status == "update") {
        uploadInProgressRef.current = true;
        thumbnailAtUploadStartRef.current = imageThumb;
        // Reset polling interval for new upload
        pollingIntervalRef.current = 1000; // Start with faster polling
      }
      
      const currentProcessingCount = fetchUploadData?.filter((pdf) => pdf.status == "processing")?.length || 0;
      const wasProcessing = previousProcessingCountRef.current > 0;
      const isProcessingComplete = wasProcessing && currentProcessingCount === 0;
      
      // Optimize: Fetch doc data only once and reuse
      let fetchedDocData = null;
      let shouldFetchDocData = false;
      
      if (
        status == "mount" &&
        docs?.doc_files_languages?.length == docs?.count_active_files
      ) {
        shouldFetchDocData = true;
      } else if (status == "update" || status == "reload") {
        // Only fetch if first file is active or we need to check thumbnail
        shouldFetchDocData = fetchUploadData[0]?.active == true || 
                           (uploadInProgressRef.current && isProcessingComplete);
      }
      
      if (shouldFetchDocData) {
        fetchedDocData = await apiGETRequest(`kdfs/${docs.id}.json`);
        if (fetchedDocData?.id != undefined) {
          docUploadDataCopy.docData = fetchedDocData;
        }
      }
      
      if (status == "mount" && fetchedDocData) {
        docUploadDataCopy.active = true;
      } else if (status == "update" || status == "reload") {
        const previousThumb = imageThumb;
        let newThumbnail = null;
        
        if (fetchedDocData?.thumb) {
          newThumbnail = fetchedDocData.thumb;
          if (newThumbnail !== imageThumb) {
            setImageThumb(newThumbnail);
          }
        }
        setReplaceSelectedPDFState(null);
        
        // Show success toast when: upload was in progress, processing completed, and thumbnail updated
        if (
          uploadInProgressRef.current &&
          isProcessingComplete &&
          currentProcessingCount === 0
        ) {
          // Use already fetched data (no duplicate API call)
          let currentThumbnail = newThumbnail || fetchedDocData?.thumb;
          
          // Show toast if thumbnail was updated from the upload start
          if (
            currentThumbnail &&
            currentThumbnail !== thumbnailAtUploadStartRef.current &&
            currentThumbnail !== noImage
          ) {
            // Small delay to ensure UI has updated (loader stopped and thumbnail rendered)
            setTimeout(() => {
              setShowNotifications({
                showToast: true,
                toastType: "success",
                toastMessage: "PDF uploaded successfully",
              });
              uploadInProgressRef.current = false;
              thumbnailAtUploadStartRef.current = null;
            }, 200);
          } else {
            // Reset tracking if thumbnail wasn't updated (upload might have failed)
            uploadInProgressRef.current = false;
            thumbnailAtUploadStartRef.current = null;
          }
        }
      }

      if (!fetchUploadData.error) {
        if (currentProcessingCount > 0) {
          // Optimize: Adaptive polling - faster initially, slower if taking long
          // Clear any existing timeout
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
          }
          
          // Gradually increase interval (1s -> 2s -> 3s) but start fast
          pollingTimeoutRef.current = setTimeout(() => {
            // Increase interval gradually but cap at 3s
            if (pollingIntervalRef.current < 3000) {
              pollingIntervalRef.current = Math.min(pollingIntervalRef.current + 500, 3000);
            }
            updateDocUploadData("reload");
          }, pollingIntervalRef.current);
        } else {
          docUploadDataCopy.active = true;
          // Reset polling interval when done
          pollingIntervalRef.current = 3000;
        }
      }

      if (Array.isArray(fetchUploadData) && fetchUploadData.length > 0 && currentProcessingCount === 0) {
        const activeFiles = fetchUploadData.filter((file) => file?.status !== "replaced" && (file?.id ?? file?.file_id));
        const activeFileIds = activeFiles.map((file) => file?.id ?? file?.file_id);
        const selectedId = selectedEditionIdRef.current;
        if ((!selectedId || !activeFileIds.includes(selectedId)) && activeFileIds[0]) {
          const sameLanguage = activeFiles.find((file) => file?.language?.locale && file.language.locale === selectedEditionLocaleRef.current);
          setSelectedEditionId(sameLanguage ? (sameLanguage.id ?? sameLanguage.file_id) : activeFileIds[0]);
        } else if (isProcessingComplete && selectedId) {
          fetchSelectedEdition(selectedId);
        }
      }

      // Update processing count ref
      previousProcessingCountRef.current = currentProcessingCount;

      // Optimize: Batch state updates
      if (fetchUploadData?.error == undefined) {
        docUploadDataCopy.pdfList = fetchUploadData;
      }
      
      // Optimize: Single state update with all changes
      const shouldUpdate = 
        (currentProcessingCount > 0 && status != "reload") ||
        (status == "reload" && 
         fetchUploadData?.error == undefined && 
         Array.isArray(fetchUploadData) && 
         fetchUploadData?.filter((pdfIT) => pdfIT.active == false)?.length == 0) ||
        (status == "update" || status != "reload");
      
      if (shouldUpdate) {
        setDocUploadData(docUploadDataCopy);
      }
    } catch (error) {
      console.warn("Error: ", error);
      // Reset upload tracking on error
      uploadInProgressRef.current = false;
      // Clear polling on error
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    }
  };

  // Optimize: Only update settings when docData actually changes
  useEffect(() => {
    if (docUploadData.docData?.id) {
      updateDocSettings(docUploadData.docData);
    }
  }, [docUploadData.docData?.id, docUploadData.docData?.page_viewed_sec, docUploadData.docData?.note]);

  const handleUpdateDocData = async (data, type = null) => {
    try {
      let dataToUpdate = {};
      if (type != null) {
        dataToUpdate = { doc: { [type]: data } };
      } else {
        dataToUpdate = data;
      }
      let uploadData = await apiJsonRequest(
        `kdfs/${docs.id}.json`,
        "PATCH",
        dataToUpdate
      );
      if (uploadData?.data != undefined) {
        if (type == "note") {
          setDocSettingsData((prevState) => {
            return {
              ...prevState,
              notes: {
                ...prevState.notes,
                value: uploadData.data.note,
                status: false,
              },
            };
          });
        } else if (type == "page_viewed_sec") {
          setDocSettingsData((prevState) => {
            return {
              ...prevState,
              engagementThreshold: {
                ...prevState.engagementThreshold,
                value: uploadData.data.page_viewed_sec,
              },
            };
          });
        }
        setDocUploadData({ ...docUploadData, docData: uploadData.data });
      }
    } catch (error) {
      console.warn(error);
    }
  };

  const handleUpdateEngagementData = () => {
    handleUpdateDocData(
      docSettingsData.engagementThreshold.value,
      "page_viewed_sec"
    );
    setDocSettingsData((prevState) => {
      return {
        ...prevState,
        engagementThreshold: {
          ...prevState.engagementThreshold,
          status: false,
        },
      };
    });
  };

  function outsideOfSettingsNote(event) {
    if (event.target != settingsNoteRef.current) {
      document.removeEventListener("click", outsideOfSettingsNote, true);

      const noteValue = settingsNoteRef.current.value;
      const noteLength = noteValue.length;

      // Validation: Check if note exceeds max length
      if (noteLength > 270) {
        setShowToast({
          showToast: true,
          toastType: 'danger',
          toastMessage: 'Note is too long. Maximum 270 characters allowed.',
        });
        // Keep edit mode open
        return;
      }

      // Save the note
      handleUpdateDocData(noteValue, "note");
    }
  }

  const handleSettingsNoteEvent = (noteStatus) => {
    // Removed outside click listener - now using explicit Save/Cancel buttons
    if (noteStatus) {
      // Store original value when entering edit mode
      originalNoteValueRef.current = docSettingsData.notes.value;
    }
    setDocSettingsData((prevState) => {
      return {
        ...prevState,
        notes: { ...prevState.notes, status: noteStatus },
      };
    });
  };

  const handleUpdateEditableTitleEvent = (e) => {
    setEditableTitle((prev) => ({ ...prev, value: e.target.value }));
  };

  const handleOutsideOrEnter = useCallback(async () => {
    try {
      if (editableTitleRef.current != null && editableTitle?.id) {
        const { data, status } = await networkService(
          `kdfs/${editableTitle.id}.json`,
          "PATCH",
          { doc: { title: editableTitleRef.current.value } }
        );
        if (status == 200) {
          setEditableTitle((prev) => ({ ...prev, status: false, value: data?.title || editableTitleRef.current.value }));
          // Update local docs state
          if (docs) {
            docs.title = data?.title || editableTitleRef.current.value;
          }
          // Refresh doc data
          updateDocUploadData("reload");
          setShowNotifications({
            showToast: true,
            toastType: "success",
            toastMessage: "Changes Saved",
          });
        }
      }
    } catch (error) {
      if (error.response?.status == 422) {
        setEditableTitle((prev) => ({
          ...prev,
          status: false,
          value: docs?.title || prev.value,
        }));
        setShowNotifications({
          showToast: true,
          toastType: "danger",
          toastMessage:
            "Sorry, KDF titles must be unique. This title already exists in your KDF library.",
        });
      }
    }
  }, [editableTitle?.id, docs, updateDocUploadData, setShowNotifications]);

  useEffect(() => {
    // Update editableTitle when docs.title changes (e.g., from external updates)
    // Only sync if not currently editing to avoid overriding user input
    if (docs?.title && docs?.title !== editableTitle.value && !editableTitle.status) {
      setEditableTitle((prev) => ({
        ...prev,
        value: docs.title,
        id: docs.id,
      }));
    }
  }, [docs?.title, docs?.id, editableTitle.value, editableTitle.status]);

  const handlePDFStatusEventTrigger = async () => {
    let recordActionData;
    if (deletePDFActiveState != null) {
      recordActionData = await customAPIRequest(
        `docs/${docs.id}/files/${deletePDFActiveState}.json`,
        "DELETE",
        {}
      );
      if (recordActionData?.data != undefined) {
        updateDocUploadData("reload");
      }
      setDeletePDFActiveState(null);
    } else if (privatePDFActiveState != null) {
      handleUpdateDocData({
        doc_id: docs.id,
        private_flag: docUploadData.docData.private_flag,
      });
      setPrivatePDFActiveState(null);
    }
  };

  useEffect(() => {
    updateDocUploadData("mount");
    return () => {
      setUploadPDFModalStatus(false);
      // Cleanup: Clear any pending polling timeouts
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!editableTitle.status) return;

    const handleClickOutside = (event) => {
      if (editableTitleRef.current && !editableTitleRef.current.contains(event.target)) {
        handleOutsideOrEnter();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        handleOutsideOrEnter();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [editableTitle.status, handleOutsideOrEnter]);

  const shouldDocUdatePDFModal = useMemo(() => {
    return docUploadData.pdfList?.length == 0;
  }, [docUploadData.pdfList?.length]);
  
  // Optimize: Memoize processing status check
  const isProcessing = useMemo(() => {
    return docUploadData?.pdfList?.some(
      (pdf) => pdf.status == "processing"
    ) || false;
  }, [docUploadData?.pdfList]);

  useEffect(() => {
    if (
      shouldDocUdatePDFModal &&
      !uploadPDFModalStatus &&
      docUploadData.active == false
    ) {
      if (uploadModalRef.current) {
        clearTimeout(uploadModalRef.current);
      }
      const timer = setTimeout(() => {
        if (!uploadPDFModalStatus) {
          uploadModalButtonRef.current.click();
        }
      }, 1000);
      uploadModalRef.current = timer;
    }

    return () => {
      if (uploadModalRef.current) {
        clearTimeout(uploadModalRef.current);
        uploadModalRef.current = null;
      }
    };
  }, [shouldDocUdatePDFModal, uploadPDFModalStatus]);

  // Track textarea resize and save to localStorage
  useEffect(() => {
    const textarea = settingsNoteRef.current;
    if (!textarea) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const newHeight = entry.target.offsetHeight;
        if (newHeight !== noteTextareaHeight) {
          setNoteTextareaHeight(newHeight);
          try {
            localStorage.setItem('kdf_note_textarea_height', newHeight.toString());
          } catch (error) {
            console.warn('Failed to save textarea height:', error);
          }
        }
      }
    });

    resizeObserver.observe(textarea);

    return () => {
      resizeObserver.disconnect();
    };
  }, [docSettingsData.notes.status, noteTextareaHeight]);

  const handleReplaceWithDialogTrigger = (itemID = null, language) => {
    if (itemID != null) {
      setReplaceSelectedPDFState({ id: itemID, language: language, replace: true });
      setUploadPDFModalStatus(true);
    }
  };

  const updateKDFData = async (data, pdfID) => {
    let updateRequest = await networkService(
      `docs/${docs.id}/files/${pdfID}.json`,
      "PATCH",
      { docs_file: data }
    );
    setDocUploadData((prevData) => ({
      ...prevData,
      pdfList: prevData.pdfList.map((pdfItem) =>
        pdfItem.id == updateRequest.data.id
          ? { ...updateRequest.data }
          : { ...pdfItem }
      ),
    }));
  };

  return (
    <>
      <CommonModal
        title={"Add a PDF To Your Kinetizine Account"}
        modalStatus={uploadPDFModalStatus}
        changeActiveStatus={(status) => {
          setUploadPDFModalStatus(status);
          handleCancelModalEvent(status);
          setReplaceSelectedPDFState(null);
        }}
        centered
        classList={"upload-kdf-modal-wrapper"}
        showFooter={false}
      >
        <UploadKdfModal
          refreshPDFList={(status) => {
            status && updateDocUploadData("update");
          }}
          closeModal={setUploadPDFModalStatus}
          userId={docs?.user?.id}
          docId={docs?.id}
          selectedFile={replaceSelectedPDFState}
        />
      </CommonModal>
      <CommonModal
        title={deletePDFActiveState != null ? "Delete File" : ""}
        modalStatus={
          deletePDFActiveState != null || privatePDFActiveState != null
        }
        changeActiveStatus={() => {
          setDeletePDFActiveState(null);
          setPrivatePDFActiveState(null);
        }}
        centered
        classList={"upload-kdf-modal-wrapper alert-modal"}
        footer={
          <div className="d-flex m-auto">
            <Button
              text={"No"}
              classList={"alert delete-btn"}
              addonClass={"my-2 mx-2"}
              onClick={() => {
                setDeletePDFActiveState(null);
                setPrivatePDFActiveState(null);
              }}
            />
            <Button
              text={"Yes"}
              classList={"success delete-btn"}
              addonClass={"my-2 mx-2 common-modal-yes-button"}
              onClick={handlePDFStatusEventTrigger}
            />
          </div>
        }
      >
        <p style={{ fontSize: "14px" }}>
          {deletePDFActiveState != null
            ? "Are you sure you want to delete this file? This action cannot be undone."
            : docUploadData.docData.private_flag
              ? "Make this KDF available to all users of this kinetizine account?"
              : "Want to set this KDF to PRIVATE? This setting will hide this KDF from other kinetizine users within your organization but your recipients will still be able to view it via the KDF Link(s) you share with them."}
        </p>
      </CommonModal>
      {/* kdf-setting-main row */}
      <div className="kdf-settings-wrapper ">
      

      {/* <div className="Language-Editions-main"> */}
        
        <Row>
          <Col md={8}>
            <Row>
              <Col md={4} >
              <div className="kdf-header-img">
                <a href={selectedEdition?.viewer || docs?.viewer_dev} rel="noopener" target="_blank">
                  <img src={loadingEdition ? noImage : (editionThumb || imageThumb)} alt="" className={loadingEdition ? "kdf-header-img--loading" : ""} />
                </a>
                <p
                  onClick={() => setUploadPDFModalStatus(true)}
                  ref={uploadModalButtonRef}
                ></p>
                {isProcessing ? (
                  <Button
                    text={"Processing..."}
                    classList={"processing"}
                    addonClass={"my-2"}
                  />
                ) : (
                  <Button
                    text={"Edit KDF"}
                    classList={`success`}
                    addonClass={"my-2 kdf-edit-set-Button"}
                    onClick={() => {
                      if (docUploadData?.pdfList?.length > 0) {
                        const editorUrl = selectedEdition?.editor_path || `/editor/${docs?.id}`;
                        window.open(editorUrl, `editor-${selectedEditionId ?? docs?.id}`);
                      }
                    }}
                    icon={<img src="/icons/edit-blue.svg" />}
                  />
                )}
                </div>
              </Col>
              <Col md={8}>
                <Row>
                  <Col md={12} className="kdf-content-wrapper">
                    <p>
                      <span>Title: </span>
                      {editableTitle.status ? (
                        <input
                          type="text"
                          value={editableTitle?.value}
                          ref={editableTitleRef}
                          onChange={(e) => handleUpdateEditableTitleEvent(e)}
                          maxLength={40}
                          className="edit-box-input"
                          style={{ minWidth: "200px", height: "auto", padding: "5px 10px" }}
                        />
                      ) : (
                        <strong
                          onClick={() =>
                            setEditableTitle((prev) => ({ ...prev, status: true }))
                          }
                          style={{ cursor: "pointer" }}
                        >
                          {docs?.title}
                        </strong>
                      )}
                    </p>
                    <p>
                      <span>Created by: </span>
                      {docs.user?.name || "Name"}
                    </p>
                    <p>
                      <span>Last Modified by: </span>
                      {docs.modifier ?? "-"}
                    </p>
                    <p>
                      <span>Orientation: </span>
                      {loadingEdition ? "Loading…" : (() => {
                        const orientation = selectedEdition?.orientation;
                        return orientation
                          ? orientation.charAt(0).toUpperCase() + orientation.slice(1)
                          : "-";
                      })()}
                    </p>
                    <div className="custom-tag-wrapper kdf-settings">
                      {loadingEdition ? (
                        <p className="kdf-settings__tagsLoading">Loading tags…</p>
                      ) : (
                        <TagChipEditor
                          tags={selectedEdition?.kdf_tags || []}
                          onChange={handleEditionTagsChange}
                          placeholderText="Add KDF tags..."
                          minWidthCh={13}
                          collapsible
                        />
                      )}
                    </div>
                    {profileData?.personal_insights && (
                      <p>
                        <CustomTooltip title={<p className="p-1 text-light">How the opt-in refers to this KDF, for example "Anna shared this proposal with you for a reason."</p>}>
                          <span>KDF type: </span>
                        </CustomTooltip>
                        <select
                          className="form-select form-select-sm d-inline-block w-auto py-0"
                          value={docUploadData?.docData?.kdf_type || "document"}
                          onChange={(e) => handleUpdateDocData(e.target.value, "kdf_type")}
                        >
                          {(profileData?.personal_insights_copy?.kdf_types || []).map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        {docUploadData?.docData?.kdf_type_source === "ai" && (
                          <span className="small text-muted ms-2">Suggested by AI</span>
                        )}
                      </p>
                    )}
                    <p>
                      <CustomTooltip title={<p className="p-1 text-light">Minimum viewing time before a page is considered viewed</p>}>
                        <span>Engagement Threshold: </span>
                      </CustomTooltip>
                      {!docSettingsData.engagementThreshold.status ? (
                        <span
                          className="edit-box"
                          onClick={() =>
                            setDocSettingsData((prevState) => {
                              return {
                                ...prevState,
                                engagementThreshold: {
                                  ...prevState.engagementThreshold,
                                  status: true,
                                },
                              };
                            })
                          }
                        >
                          {docSettingsData.engagementThreshold.value}
                        </span>
                      ) : (
                        <>
                          <input
                            className="edit-box-input"
                            type="number"
                            value={docSettingsData.engagementThreshold.value}
                            onChange={(e) =>
                              setDocSettingsData((prevState) => {
                                return {
                                  ...prevState,
                                  engagementThreshold: {
                                    ...prevState.engagementThreshold,
                                    value: e.target.value,
                                  },
                                };
                              })
                            }
                          />
                          <CustomTooltip title={<p className="p-1 text-light">Save Changes</p>}>
                            <FontAwesomeIcon
                              icon={faFloppyDisk}
                              onClick={handleUpdateEngagementData}
                              className="active mx-2 edit-icon"
                            />
                          </CustomTooltip>
                          <CustomTooltip title={<p className="p-1 text-light">Cancel</p>}>
                            <FontAwesomeIcon
                              icon={faRectangleXmark}
                              onClick={() => {
                                setDocSettingsData((prevState) => {
                                  return {
                                    ...prevState,
                                    engagementThreshold: {
                                      ...prevState.engagementThreshold,
                                      status: false,
                                      value:
                                        docUploadData.docData.page_viewed_sec,
                                    },
                                  };
                                });
                              }}
                              className="cancel mx-2 edit-icon"
                            />
                          </CustomTooltip>
                        </>
                      )}
                      <span> seconds</span>
                    </p>
                    <Form.Check
                      type="switch"
                      id="custom-switch"
                      label="Private"
                      className="d-inline-block kdf-setting-switch"
                      checked={
                        docUploadData?.docData?.private_flag != undefined
                          ? docUploadData?.docData?.private_flag
                          : false
                      }
                      onChange={() => setPrivatePDFActiveState(docs.id)}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
          <Col md={4}>
            <div className="mb-2 d-flex align-items-center justify-content-between">
              <span className="notes-kdf">Notes:</span>
              <div className="notes-action-buttons">
                {!docSettingsData.notes.status ? (
                  <button
                    onClick={() => handleSettingsNoteEvent(true)}
                    className="note-icon-btn"
                    title={docSettingsData?.notes?.value ? "Edit Note" : "Add Note"}
                  >
                    {docSettingsData?.notes?.value ? (
                      <img src="/icons/edit-blue.svg" alt="Edit" className="note-action-icon" />
                    ) : (
                      <svg className="note-action-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    )}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        const noteValue = settingsNoteRef.current.value;
                        if (noteValue.length > 270) {
                          setShowToast({
                            showToast: true,
                            toastType: 'danger',
                            toastMessage: 'Note is too long. Maximum 270 characters allowed.',
                          });
                          return;
                        }
                        handleUpdateDocData(noteValue, "note");
                        handleSettingsNoteEvent(false);
                      }}
                      className="note-icon-btn note-save-btn"
                      title="Save Note"
                    >
                      <svg className="note-action-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        setDocSettingsData((prevState) => {
                          return {
                            ...prevState,
                            notes: {
                              ...prevState.notes,
                              status: false,
                              value: originalNoteValueRef.current || ''
                            },
                          };
                        });
                      }}
                      className="note-icon-btn note-cancel-btn"
                      title="Cancel"
                    >
                      <svg className="note-action-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </>
                )}
              </div>
            </div>
            {!docSettingsData.notes.status ? (
              <span className="d-flex mb-1 note-content-display" style={{ whiteSpace: 'pre-line' }}>
                {docSettingsData?.notes?.value || <span className="note-empty-state">No notes added</span>}
              </span>
            ) : (
              <>
                <Form.Control
                  as="textarea"
                  placeholder="Add notes about this KDF (max 270 characters)"
                  style={{
                    height: `${noteTextareaHeight}px`,
                    maxHeight: '200px'
                  }}
                  value={docSettingsData.notes.value || ''}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    // Enforce 270 character limit
                    if (newValue.length <= 270) {
                      setDocSettingsData((prevState) => {
                        return {
                          ...prevState,
                          notes: { ...prevState.notes, value: newValue },
                        };
                      });
                    }
                  }}
                  ref={settingsNoteRef}
                  autoFocus
                  className={
                    (docSettingsData.notes.value?.length || 0) <= 200 ? 'note-char-ok' :
                    (docSettingsData.notes.value?.length || 0) <= 260 ? 'note-char-warning' :
                    'note-char-danger'
                  }
                />
                <div className="note-char-counter">
                  <span className={
                    (docSettingsData.notes.value?.length || 0) <= 200 ? 'counter-ok' :
                    (docSettingsData.notes.value?.length || 0) <= 260 ? 'counter-warning' :
                    'counter-danger'
                  }>
                    {docSettingsData.notes.value?.length || 0}/270 characters
                  </span>
                </div>
              </>
            )}
          </Col>
        </Row>
      </div>

      {/* ── Background Settings ────────────────────────────────────── */}
      <div className="kdf-settings-wrapper mt-3" style={{ padding: '16px 20px' }}>
        {/* Section header */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <span className="notes-kdf" style={{ fontWeight: 600 }}>Background</span>
          <div className="d-flex align-items-center gap-3">
            {/* Style transfer group */}
            <div className="d-flex align-items-center gap-1">
              <CustomTooltip title={<p className="p-1 text-light" style={{ fontSize: '12px', margin: 0 }}>Use <strong>Copy</strong> to save this background style, then open another KDF's settings and click <strong>Apply</strong> to use it there.</p>}>
                <img src="/icons/info-color.svg" alt="info" style={{ width: 16, height: 16, cursor: 'default', display: 'block', marginRight: 2 }} />
              </CustomTooltip>
              <button
                onClick={handleBgCopy}
                title="Copy background style to clipboard"
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  fontSize: '12px', padding: '3px 8px', borderRadius: 4,
                  border: '1px solid #d0d0d0', background: '#f7f7f7',
                  cursor: 'pointer', color: bgCopied ? '#27ae60' : '#555',
                  fontWeight: bgCopied ? 600 : 400, transition: 'color 0.2s'
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
                {bgCopied ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={handleBgPaste}
                title={bgPasteStatus === 'err' ? 'Nothing valid in clipboard — copy a style from another KDF first' : 'Apply background style from clipboard'}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  fontSize: '12px', padding: '3px 8px', borderRadius: 4,
                  border: '1px solid #d0d0d0', background: '#f7f7f7',
                  cursor: 'pointer',
                  color: bgPasteStatus === 'err' ? '#c0392b' : bgPasteStatus === 'ok' ? '#27ae60' : '#555',
                  fontWeight: (bgPasteStatus === 'ok' || bgPasteStatus === 'err') ? 600 : 400,
                  transition: 'color 0.2s'
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                </svg>
                {bgPasteStatus === 'ok' ? 'Applied!' : bgPasteStatus === 'err' ? 'Nothing found' : 'Apply'}
              </button>
            </div>

            {/* Divider */}
            <div style={{ width: 1, height: 20, background: '#e0e0e0' }} />

            {/* Save — gray/disabled until there's an actual unsaved change */}
            <Button
              classList="success"
              addonClass="bg-save-btn"
              text={bgSaving ? 'Saving…' : 'Save'}
              onClick={handleBgSave}
              disabled={bgSaving || !bgDirty}
            />
          </div>
        </div>

        <div className="d-flex gap-4 align-items-start">
          {/* Left: pattern picker + controls */}
          <div className="flex-grow-1">
            {/* Pattern grid */}
            <div className="mb-3">
              <span style={{ fontSize: '11px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>Pattern</span>
              <div className="d-flex flex-wrap gap-2">
                {BG_PATTERNS.map(pat => {
                  const isSelected = (viewerBgSettings.pattern_type || 'herringbone') === pat.id;
                  return (
                    <div
                      key={pat.id}
                      title={pat.name}
                      onClick={() => handleBgChange('pattern_type', pat.id)}
                      style={{
                        width: 52, height: 36, borderRadius: 6, cursor: 'pointer', flexShrink: 0,
                        border: isSelected ? '2px solid #3d85c8' : '1px solid #d0d0d0',
                        boxShadow: isSelected ? '0 0 0 3px rgba(61,133,200,0.15)' : 'none',
                        transition: 'border 0.15s, box-shadow 0.15s',
                        ...buildPatternStyle(pat.id, viewerBgSettings.bg_color, viewerBgSettings.pattern_color, viewerBgSettings.pattern_opacity),
                      }}
                    />
                  );
                })}
              </div>
            </div>

            {/* Color + opacity controls */}
            <div className="d-flex align-items-end gap-4 flex-wrap">
              {/* BG Color */}
              <div>
                <span style={{ fontSize: '11px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>BG Color</span>
                <ColorPicker
                  color={viewerBgSettings.bg_color}
                  onChange={(val) => handleBgChange('bg_color', val)}
                  alpha={false}
                />
              </div>

              {/* Pattern Color */}
              <div>
                <span style={{ fontSize: '11px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Pattern Color</span>
                <ColorPicker
                  color={viewerBgSettings.pattern_color}
                  onChange={(val) => handleBgChange('pattern_color', val)}
                  alpha={false}
                />
              </div>

              {/* Opacity */}
              <div>
                <div className="d-flex align-items-center gap-2" style={{ marginBottom: 6 }}>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Opacity</span>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: '#3d85c8' }}>{Math.round(viewerBgSettings.pattern_opacity * 100)}%</span>
                </div>
                <input
                  type="range" min="0" max="1" step="0.05"
                  value={viewerBgSettings.pattern_opacity}
                  onChange={e => handleBgChange('pattern_opacity', parseFloat(e.target.value))}
                  style={{ width: 120, display: 'block' }}
                />
              </div>
            </div>
          </div>

          {/* Right: preview */}
          <div style={{ flexShrink: 0 }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}>Preview</span>
            <div
              style={{
                width: 130, height: 88, borderRadius: 8,
                border: '1px solid #d0d0d0',
                boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                ...buildPatternStyle(
                  viewerBgSettings.pattern_type || 'herringbone',
                  viewerBgSettings.bg_color,
                  viewerBgSettings.pattern_color,
                  viewerBgSettings.pattern_opacity
                ),
              }}
            />
          </div>
        </div>
      </div>

      <div className="m-0">
        <KDFSettingsPageListing
          PdfList={
            Array.isArray(docUploadData.pdfList)
              ? docUploadData.pdfList
              : docUploadData.docData.doc_files_languages
          }
          handleUploadPDFModalStatus={(status) =>
            setUploadPDFModalStatus(status)
          }
          docs={docs}
          handleSetReplaceWith={handleReplaceWithDialogTrigger}
          handleDeletePDFEvent={setDeletePDFActiveState}
          handleUpdateDocStatus={updateKDFData}
          selectedEditionId={selectedEditionId}
          onSelectEdition={handleSelectEdition}
        />
      </div>
    </>
  );
};
export default React.memo(KdfSettings);
